import React, { useState, useEffect } from 'react';
import { Check, X } from '../../icons';
import Button from '../ui/Button';
import { CardSection } from '../ui/Card';

const CacheSettings = ({ cacheConfig, isLoading, onSave, userId }) => {
  const [formData, setFormData] = useState({
    analysisCacheTTLDays: 30,
    comparisonCacheTTLDays: 30,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  useEffect(() => {
    if (cacheConfig) {
      setFormData({
        analysisCacheTTLDays: cacheConfig.analysisCacheTTLDays,
        comparisonCacheTTLDays: cacheConfig.comparisonCacheTTLDays,
      });
      setHasChanges(false);
    }
  }, [cacheConfig]);

  const handleChange = (field, value) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) return;
    const clamped = Math.min(365, Math.max(1, numValue));
    setFormData(prev => ({ ...prev, [field]: clamped }));
    setHasChanges(true);
    setSaveMessage(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      await onSave(formData, userId);
      setHasChanges(false);
      setSaveMessage({ type: 'success', text: 'Cache settings saved successfully!' });
    } catch (error) {
      console.error('Error saving cache settings:', error);
      setSaveMessage({ type: 'error', text: `Failed to save: ${error.message}` });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (cacheConfig) {
      setFormData({
        analysisCacheTTLDays: cacheConfig.analysisCacheTTLDays,
        comparisonCacheTTLDays: cacheConfig.comparisonCacheTTLDays,
      });
      setHasChanges(false);
      setSaveMessage(null);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-card border border-line rounded-card p-4 animate-pulse space-y-3" aria-busy="true">
        <div className="h-5 bg-card-2 rounded w-1/3" />
        <div className="h-11 bg-card-2 rounded-xl" />
      </div>
    );
  }

  return (
    <CardSection title="Cache TTL" className="space-y-4">
      <p className="text-sm text-secondary -mt-1">
        How long AI analysis and comparison results are cached before expiring.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label" htmlFor="cache-analysis">Analysis cache (days)</label>
          <input
            id="cache-analysis"
            type="number"
            inputMode="numeric"
            min={1}
            max={365}
            value={formData.analysisCacheTTLDays}
            onChange={(e) => handleChange('analysisCacheTTLDays', e.target.value)}
            className="input"
          />
        </div>
        <div>
          <label className="label" htmlFor="cache-comparison">Comparison cache (days)</label>
          <input
            id="cache-comparison"
            type="number"
            inputMode="numeric"
            min={1}
            max={365}
            value={formData.comparisonCacheTTLDays}
            onChange={(e) => handleChange('comparisonCacheTTLDays', e.target.value)}
            className="input"
          />
        </div>
      </div>

      {saveMessage && (
        <div
          role="status"
          className={`p-3 rounded-xl text-sm border ${
            saveMessage.type === 'success' ? 'bg-accent-soft text-accent border-accent/30' : 'bg-danger-soft text-danger border-danger/30'
          }`}
        >
          {saveMessage.text}
        </div>
      )}

      <div className="flex gap-2">
        <Button variant="primary" onClick={handleSave} disabled={!hasChanges || isSaving} loading={isSaving}>
          {!isSaving && <Check className="w-4 h-4" />}
          {isSaving ? 'Saving…' : 'Save changes'}
        </Button>
        <Button variant="ghost" onClick={handleReset} disabled={!hasChanges || isSaving}>
          <X className="w-4 h-4" /> Reset
        </Button>
      </div>
    </CardSection>
  );
};

export default CacheSettings;
